import React, { useState, useEffect } from 'react';
import { loyaltyPortalAPI, benefitsAPI, redemptionsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { Award, Gift, Star, ShoppingBag, MessageSquare, Clock, CheckCircle, Heart, Zap, ChevronRight, Filter, Search, X, Play, Image as ImageIcon, FileText } from 'lucide-react';

const STATUS_CONFIG = {
  PENDIENTE: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  EN_PROCESO: { label: 'En Proceso', color: 'bg-blue-100 text-blue-800', icon: Clock },
  ENTREGADO: { label: 'Entregado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  CANCELADO: { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: Clock },
};

// Promotional Banner Component
const PromoBanner = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const banners = [
    {
      title: "Duplica tus puntos",
      subtitle: "En todas las compras de febrero",
      gradient: "from-purple-600 to-indigo-600",
      image: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=800&q=80"
    },
    {
      title: "Nuevos Beneficios",
      subtitle: "Descubre las nuevas recompensas disponibles",
      gradient: "from-emerald-500 to-teal-600",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80"
    },
    {
      title: "Top Proveedores",
      subtitle: "Compite por el primer lugar del ranking",
      gradient: "from-amber-500 to-orange-600",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [banners.length]);

  return (
    <div className="relative overflow-hidden rounded-2xl h-48 mb-6">
      {banners.map((banner, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-all duration-700 ease-in-out ${
            idx === currentSlide ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
          }`}
        >
          <div className={`absolute inset-0 bg-gradient-to-r ${banner.gradient}`} />
          <div 
            className="absolute inset-0 opacity-20 bg-cover bg-center"
            style={{ backgroundImage: `url(${banner.image})` }}
          />
          <div className="relative h-full flex items-center px-8">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-2">{banner.title}</h2>
              <p className="text-white/80 text-lg">{banner.subtitle}</p>
            </div>
          </div>
        </div>
      ))}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {banners.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentSlide ? 'bg-white w-6' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Visual Badge Card for Newsfeed
const BadgeNewsfeedCard = ({ item, formatDate }) => {
  const [liked, setLiked] = useState(false);
  
  return (
    <Card className="overflow-hidden bg-gradient-to-b from-slate-50 to-white border-0 shadow-lg" data-testid={`feed-badge-${item.id}`}>
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex justify-between items-center px-6 pt-4">
          <Badge className="bg-primary text-white px-4 py-1 text-sm font-medium">
            Insignia
          </Badge>
          <span className="text-slate-500 text-sm">{formatDate(item.created_at)}</span>
        </div>
        
        {/* Medal Visual */}
        <div className="flex flex-col items-center py-8">
          <div className="relative">
            {/* Ribbon */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-16 h-20">
              <div className="w-full h-full bg-gradient-to-b from-primary via-blue-500 to-primary" 
                   style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 50% 80%, 15% 100%)' }} />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-16 bg-gradient-to-b from-white/30 to-transparent"
                   style={{ clipPath: 'polygon(20% 0, 80% 0, 70% 100%, 50% 85%, 30% 100%)' }} />
            </div>
            {/* Medal */}
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-yellow-300 via-yellow-400 to-amber-500 shadow-xl flex items-center justify-center mt-8 ring-4 ring-yellow-200">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-200 via-yellow-300 to-amber-400 flex items-center justify-center shadow-inner">
                <Star className="w-12 h-12 text-amber-600 fill-amber-500" />
              </div>
            </div>
          </div>
          
          {/* Badge Name */}
          <h3 className="text-2xl font-bold text-primary mt-6 text-center px-4">
            {item.title.replace('Nueva insignia: ', '')}
          </h3>
          
          {/* Points Badge */}
          {item.points && (
            <div className="flex items-center gap-1 mt-3">
              <Badge className="bg-amber-100 text-amber-700 px-4 py-1.5 text-lg font-bold">
                +{item.points} puntos
              </Badge>
            </div>
          )}
          
          {/* Category Pills */}
          {item.category && (
            <div className="flex gap-2 mt-4">
              <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                {item.category}
              </span>
            </div>
          )}
        </div>
        
        {/* Description */}
        <div className="px-6 pb-4">
          <p className="text-slate-600 text-center leading-relaxed">
            {item.content || 'Felicidades por obtener esta insignia. Sigue acumulando puntos y desbloqueando nuevas recompensas.'}
          </p>
        </div>
        
        {/* Reactions */}
        <div className="border-t px-6 py-4 flex items-center gap-4">
          <button 
            onClick={() => setLiked(!liked)}
            className={`flex items-center gap-1.5 transition-colors ${liked ? 'text-red-500' : 'text-slate-400 hover:text-red-400'}`}
          >
            <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
          </button>
          <button className="text-slate-400 hover:text-amber-500 transition-colors">
            <Zap className="w-5 h-5" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

// Communication Card for Newsfeed
const CommunicationCard = ({ item, formatDate }) => {
  const hasMedia = item.media_urls && item.media_urls.length > 0;
  const isVideo = hasMedia && item.media_urls[0]?.includes('video');
  
  return (
    <Card className="overflow-hidden" data-testid={`feed-comm-${item.id}`}>
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex justify-between items-center px-6 pt-4">
          <Badge variant="outline" className="px-4 py-1 text-sm font-medium border-blue-200 text-blue-600">
            Comunicado
          </Badge>
          <span className="text-slate-500 text-sm">{formatDate(item.created_at)}</span>
        </div>
        
        {/* Media Preview */}
        {hasMedia && (
          <div className="mt-4 mx-6 rounded-xl overflow-hidden bg-slate-100 relative aspect-video">
            {isVideo ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                <Play className="w-16 h-16 text-white opacity-80" />
              </div>
            ) : (
              <img 
                src={item.media_urls[0]} 
                alt="Media" 
                className="w-full h-full object-cover"
              />
            )}
          </div>
        )}
        
        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
          <p className="text-slate-600 leading-relaxed">{item.content}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Benefit Card with Visual Design
const BenefitCard = ({ benefit, onSelect, canRedeem }) => {
  return (
    <Card 
      className={`overflow-hidden cursor-pointer transition-all hover:shadow-xl hover:-translate-y-1 ${!canRedeem ? 'opacity-60' : ''}`}
      onClick={() => onSelect(benefit)}
      data-testid={`benefit-card-${benefit.id}`}
    >
      <CardContent className="p-0">
        {/* Image or Gradient */}
        <div className="h-40 relative overflow-hidden">
          {benefit.image_url ? (
            <img 
              src={benefit.image_url} 
              alt={benefit.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Gift className="w-16 h-16 text-white/50" />
            </div>
          )}
          {/* Points Badge Overlay */}
          <div className="absolute top-3 right-3">
            <Badge className="bg-white text-emerald-600 shadow-lg px-3 py-1.5 text-sm font-bold">
              <Star className="w-4 h-4 mr-1 fill-current" />
              {benefit.points_cost}
            </Badge>
          </div>
          {/* Category Tag */}
          {benefit.category && (
            <div className="absolute bottom-3 left-3">
              <Badge className="bg-black/50 text-white text-xs">
                {benefit.category}
              </Badge>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-4">
          <h3 className="font-bold text-slate-900 mb-1 line-clamp-1">{benefit.name}</h3>
          <p className="text-sm text-slate-500 line-clamp-2">{benefit.description}</p>
          
          <div className="mt-4 flex justify-between items-center">
            <span className={`text-sm font-medium ${canRedeem ? 'text-emerald-600' : 'text-slate-400'}`}>
              {canRedeem ? 'Disponible' : 'Puntos insuficientes'}
            </span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Benefit Detail Dialog
const BenefitDetailDialog = ({ benefit, open, onClose, onRedeem, canRedeem, availablePoints }) => {
  if (!benefit) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        {/* Header Image */}
        <div className="h-48 relative">
          {benefit.image_url ? (
            <img 
              src={benefit.image_url} 
              alt={benefit.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Gift className="w-20 h-20 text-white/50" />
            </div>
          )}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{benefit.name}</h2>
              {benefit.category && (
                <Badge variant="outline" className="mt-2">{benefit.category}</Badge>
              )}
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-2xl font-bold text-emerald-600">
                <Star className="w-6 h-6 fill-current" />
                {benefit.points_cost}
              </div>
              <span className="text-sm text-slate-500">puntos</span>
            </div>
          </div>
          
          <p className="text-slate-600 mb-6">{benefit.description}</p>
          
          {/* Terms & Conditions */}
          {benefit.terms_conditions && (
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Términos y Condiciones
              </h4>
              <p className="text-sm text-slate-600">{benefit.terms_conditions}</p>
            </div>
          )}
          
          {/* Points Balance */}
          <div className="border-t pt-4 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Tus puntos disponibles</span>
              <span className="font-semibold">{availablePoints}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-slate-500">Costo del beneficio</span>
              <span className="font-semibold text-emerald-600">-{benefit.points_cost}</span>
            </div>
            <div className="flex justify-between text-sm mt-2 pt-2 border-t">
              <span className="text-slate-700 font-medium">Puntos después del canje</span>
              <span className={`font-bold ${canRedeem ? 'text-emerald-600' : 'text-red-500'}`}>
                {availablePoints - benefit.points_cost}
              </span>
            </div>
          </div>
          
          <Button 
            onClick={() => onRedeem(benefit)}
            disabled={!canRedeem}
            className="w-full"
            size="lg"
            data-testid="confirm-redeem-detail-btn"
          >
            {canRedeem ? (
              <>
                <Gift className="w-5 h-5 mr-2" />
                Canjear Ahora
              </>
            ) : (
              'Puntos Insuficientes'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function LoyaltyPortalPage() {
  const [pointsSummary, setPointsSummary] = useState(null);
  const [badges, setBadges] = useState([]);
  const [newsfeed, setNewsfeed] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Filters for benefits
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [maxPoints, setMaxPoints] = useState(1000);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pointsRes, badgesRes, feedRes, benefitsRes, redemptionsRes] = await Promise.all([
        loyaltyPortalAPI.getMyPoints(),
        loyaltyPortalAPI.getMyBadges(),
        loyaltyPortalAPI.getNewsfeed(),
        benefitsAPI.getAll(),
        loyaltyPortalAPI.getMyRedemptions()
      ]);
      setPointsSummary(pointsRes.data);
      setBadges(badgesRes.data);
      setNewsfeed(feedRes.data);
      setBenefits(benefitsRes.data);
      setRedemptions(redemptionsRes.data);
      
      // Set max points based on highest benefit
      const highestCost = Math.max(...benefitsRes.data.map(b => b.points_cost), 500);
      setMaxPoints(highestCost);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (benefit) => {
    try {
      await redemptionsAPI.create(benefit.id);
      toast.success('Canje solicitado exitosamente');
      setShowDetailDialog(false);
      setSelectedBenefit(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al canjear');
    }
  };

  const openBenefitDetail = (benefit) => {
    setSelectedBenefit(benefit);
    setShowDetailDialog(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-EC', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Get unique categories
  const categories = [...new Set(benefits.map(b => b.category).filter(Boolean))];
  
  // Filter benefits
  const filteredBenefits = benefits.filter(b => {
    const matchesSearch = b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         b.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || b.category === categoryFilter;
    const matchesPoints = b.points_cost <= maxPoints;
    return matchesSearch && matchesCategory && matchesPoints;
  });

  if (loading) {
    return <div className="flex justify-center items-center h-64">Cargando...</div>;
  }

  const availablePoints = pointsSummary?.available_points || 0;

  return (
    <div className="space-y-6" data-testid="loyalty-portal-page">
      {/* Promotional Banner */}
      <PromoBanner />
      
      {/* Points Summary Header */}
      <div className="bg-gradient-to-r from-primary to-blue-600 rounded-2xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Programa de Fidelización</h1>
            <p className="text-blue-100 mt-1">{pointsSummary?.provider_company_name}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2">
              <Star className="w-10 h-10 fill-yellow-400 text-yellow-400" />
              <span className="text-4xl font-bold">{availablePoints}</span>
            </div>
            <p className="text-blue-100 text-sm">Puntos disponibles</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">{pointsSummary?.total_badges || 0}</p>
            <p className="text-sm text-blue-100">Insignias</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">{pointsSummary?.total_points_earned || 0}</p>
            <p className="text-sm text-blue-100">Puntos Ganados</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">{pointsSummary?.total_points_spent || 0}</p>
            <p className="text-sm text-blue-100">Puntos Canjeados</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="newsfeed" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="newsfeed" data-testid="tab-newsfeed">
            <MessageSquare className="w-4 h-4 mr-2" />
            Novedades
          </TabsTrigger>
          <TabsTrigger value="badges" data-testid="tab-badges">
            <Award className="w-4 h-4 mr-2" />
            Mis Insignias
          </TabsTrigger>
          <TabsTrigger value="benefits" data-testid="tab-benefits">
            <Gift className="w-4 h-4 mr-2" />
            Canjear
          </TabsTrigger>
          <TabsTrigger value="redemptions" data-testid="tab-redemptions">
            <ShoppingBag className="w-4 h-4 mr-2" />
            Mis Canjes
          </TabsTrigger>
        </TabsList>

        {/* Newsfeed Tab - Redesigned */}
        <TabsContent value="newsfeed" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {newsfeed.map(item => (
              item.type === 'badge_earned' ? (
                <BadgeNewsfeedCard key={item.id} item={item} formatDate={formatDate} />
              ) : (
                <CommunicationCard key={item.id} item={item} formatDate={formatDate} />
              )
            ))}
          </div>
          {newsfeed.length === 0 && (
            <div className="text-center py-16 text-slate-500">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No hay novedades aún</p>
              <p className="text-sm">Las insignias ganadas y comunicados aparecerán aquí</p>
            </div>
          )}
        </TabsContent>

        {/* Badges Tab - Redesigned */}
        <TabsContent value="badges" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {badges.map(badge => (
              <Card key={badge.id} className="overflow-hidden bg-gradient-to-b from-slate-50 to-white" data-testid={`my-badge-${badge.id}`}>
                <CardContent className="p-0">
                  <div className="flex flex-col items-center py-8">
                    {/* Medal Visual */}
                    <div className="relative">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-12 h-16">
                        <div className="w-full h-full bg-gradient-to-b from-primary via-blue-500 to-primary" 
                             style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 50% 80%, 15% 100%)' }} />
                      </div>
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-300 via-yellow-400 to-amber-500 shadow-lg flex items-center justify-center mt-6 ring-2 ring-yellow-200">
                        <div className="w-18 h-18 rounded-full bg-gradient-to-br from-yellow-200 via-yellow-300 to-amber-400 flex items-center justify-center">
                          <Star className="w-10 h-10 text-amber-600 fill-amber-500" />
                        </div>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-primary mt-4 text-center px-4">
                      {badge.badge_name}
                    </h3>
                    
                    <Badge variant="outline" className="mt-2 text-xs">
                      {badge.badge_category}
                    </Badge>
                    
                    <div className="flex items-center gap-1 text-amber-600 font-bold mt-2">
                      <Star className="w-4 h-4 fill-current" />
                      {badge.badge_points} pts
                    </div>
                    
                    <p className="text-xs text-slate-400 mt-2">
                      Obtenida: {formatDate(badge.assigned_at)}
                    </p>
                  </div>
                  
                  {badge.message && (
                    <div className="border-t px-4 py-3 bg-slate-50">
                      <p className="text-sm text-slate-600 text-center italic">
                        "{badge.message}"
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          {badges.length === 0 && (
            <div className="text-center py-16 text-slate-500">
              <Award className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Aún no tienes insignias</p>
              <p className="text-sm">Sigue participando para ganar insignias</p>
            </div>
          )}
        </TabsContent>

        {/* Benefits Tab - With Filters */}
        <TabsContent value="benefits" className="mt-6">
          {/* Search and Filters */}
          <div className="mb-6 space-y-4">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Buscar beneficios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-primary text-white' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtros
              </Button>
            </div>
            
            {showFilters && (
              <Card className="p-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Categoría</label>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las categorías</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Máximo de puntos: {maxPoints}
                    </label>
                    <Slider
                      value={[maxPoints]}
                      onValueChange={([val]) => setMaxPoints(val)}
                      max={Math.max(...benefits.map(b => b.points_cost), 500)}
                      min={0}
                      step={10}
                      className="mt-2"
                    />
                  </div>
                </div>
              </Card>
            )}
          </div>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredBenefits.map(benefit => (
              <BenefitCard
                key={benefit.id}
                benefit={benefit}
                onSelect={openBenefitDetail}
                canRedeem={availablePoints >= benefit.points_cost}
              />
            ))}
          </div>
          
          {filteredBenefits.length === 0 && (
            <div className="text-center py-16 text-slate-500">
              <Gift className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No hay beneficios que coincidan</p>
              <p className="text-sm">Intenta ajustar los filtros</p>
            </div>
          )}
        </TabsContent>

        {/* Redemptions Tab */}
        <TabsContent value="redemptions" className="mt-6">
          <div className="space-y-4">
            {redemptions.map(redemption => {
              const statusConfig = STATUS_CONFIG[redemption.status];
              const StatusIcon = statusConfig?.icon || Clock;
              return (
                <Card key={redemption.id} data-testid={`redemption-${redemption.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                          <ShoppingBag className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{redemption.benefit_name}</h3>
                          <p className="text-sm text-slate-500">{formatDate(redemption.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1 text-amber-600 font-semibold">
                          <Star className="w-5 h-5 fill-current" />
                          {redemption.points_spent} pts
                        </div>
                        <Badge className={statusConfig?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig?.label}
                        </Badge>
                      </div>
                    </div>
                    {redemption.admin_notes && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">
                          <strong>Nota:</strong> {redemption.admin_notes}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {redemptions.length === 0 && (
            <div className="text-center py-16 text-slate-500">
              <ShoppingBag className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No has canjeado ningún beneficio</p>
              <p className="text-sm">Explora el catálogo y canjea tus puntos</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Benefit Detail Dialog */}
      <BenefitDetailDialog
        benefit={selectedBenefit}
        open={showDetailDialog}
        onClose={() => setShowDetailDialog(false)}
        onRedeem={handleRedeem}
        canRedeem={selectedBenefit ? availablePoints >= selectedBenefit.points_cost : false}
        availablePoints={availablePoints}
      />
    </div>
  );
}
